import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { RiCheckLine, RiExternalLinkLine, RiFileCopyLine, RiRefreshLine } from "@remixicon/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { FullPageLoader } from "@/components/ui/full-page-loader";
import { PageHeader } from "@/components/dashboard/shared/PageHeader";
import { SectionCard } from "@/components/dashboard/shared/SectionCard";
import { DashboardHead } from "@/components/SEO/DashboardHead";
import { BannerUpload } from "@/components/ui/banner-upload";
import { useSettings, type StoreCustomization } from "@/contexts/SettingsContext";
import { useCurrentStore } from "@/hooks/useCurrentStore";
import { cn } from "@/lib/utils";
import {
  PREVIEW_MESSAGE,
  PREVIEW_READY,
  SALES_LINK_TEMPLATES,
  normalizeTemplateId,
  templateToStoredName,
  type SalesLinkDraft,
  type SalesLinkTemplateId,
} from "@/lib/sales-link";

type Draft = SalesLinkDraft & { bannerImageData?: string };

function draftFrom(customization: StoreCustomization): Draft {
  return {
    templateId: normalizeTemplateId(
      customization.salesLink?.activeTemplateId || customization.template,
    ),
    colors: {
      primary: customization.colors?.primary || "#6D28D9",
      secondary: customization.colors?.secondary || "#6366f1",
      accent: customization.colors?.accent || "#f43f5e",
    },
    hero: {
      title: customization.hero?.title || "",
      subtitle: customization.hero?.subtitle || "",
    },
    announcements: {
      enabled: Boolean(customization.announcements?.enabled),
      text: customization.announcements?.text || "",
      backgroundColor: customization.announcements?.backgroundColor || "#fef3c7",
      textColor: customization.announcements?.textColor || "#92400e",
    },
    layoutOptions: {
      showHero: customization.layoutOptions?.showHero !== false,
      showCategories: customization.layoutOptions?.showCategories !== false,
      showSearch: customization.layoutOptions?.showSearch !== false,
      showSocialLinks: customization.layoutOptions?.showSocialLinks !== false,
    },
    banner: {
      enabled: Boolean(customization.banner?.enabled),
      imageUrl: customization.banner?.imageUrl || "",
    },
  };
}

function previewDraft(draft: Draft): SalesLinkDraft {
  return {
    templateId: draft.templateId,
    colors: draft.colors,
    hero: draft.hero,
    announcements: draft.announcements,
    layoutOptions: draft.layoutOptions,
    banner: {
      enabled: draft.banner.enabled,
      imageUrl:
        draft.banner.enabled && draft.bannerImageData
          ? draft.bannerImageData
          : draft.banner.imageUrl,
    },
  };
}

export function SalesLinkBuilderPage() {
  const { customization, updateCustomization, isLoading, isUpdating } = useSettings();
  const { currentStore, storeId } = useCurrentStore();
  const frameRef = useRef<HTMLIFrameElement>(null);
  const draftRef = useRef<Draft | null>(null);
  const [ready, setReady] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [savedId, setSavedId] = useState<SalesLinkTemplateId>("classic");
  const [baseline, setBaseline] = useState("");
  const [frameReady, setFrameReady] = useState(false);
  const [frameKey, setFrameKey] = useState(0);

  useEffect(() => {
    setReady(false);
    setFrameReady(false);
  }, [storeId]);

  useEffect(() => {
    if (isLoading || ready) return;
    const next = draftFrom(customization);
    setDraft(next);
    setSavedId(next.templateId);
    setBaseline(JSON.stringify(previewDraft(next)));
    setReady(true);
  }, [isLoading, ready, customization]);

  draftRef.current = draft;
  const dirty = ready && draft != null && baseline !== JSON.stringify(previewDraft(draft));
  const publicLink = currentStore?.url ? `https://sqale.shop/${currentStore.url}` : "";

  const postDraft = (target?: Window | null) => {
    const current = draftRef.current;
    if (!current || !target) return;
    target.postMessage(
      { type: PREVIEW_MESSAGE, draft: previewDraft(current) },
      window.location.origin,
    );
  };

  useEffect(() => {
    if (!dirty) return;
    const onLeave = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as Element | null)?.closest("a");
      if (!anchor || anchor.getAttribute("target") === "_blank") return;
      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#")) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      if (window.confirm("You have unsaved sales link changes. Leave without saving?")) return;
      event.preventDefault();
      event.stopPropagation();
    };
    window.addEventListener("beforeunload", onLeave);
    document.addEventListener("click", onClick, true);
    return () => {
      window.removeEventListener("beforeunload", onLeave);
      document.removeEventListener("click", onClick, true);
    };
  }, [dirty]);

  useEffect(() => {
    if (!draft || !frameReady) return;
    postDraft(frameRef.current?.contentWindow);
  }, [draft, frameReady, frameKey]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.source !== frameRef.current?.contentWindow) return;
      if (event.data?.type !== PREVIEW_READY) return;
      setFrameReady(true);
      postDraft(event.source as Window);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const save = async () => {
    if (!draft) return;
    const saved = await updateCustomization({
      template: templateToStoredName(draft.templateId),
      colors: draft.colors,
      hero: draft.hero,
      announcements: draft.announcements,
      layoutOptions: draft.layoutOptions,
      banner: {
        enabled: draft.banner.enabled,
        ...(draft.banner.imageUrl && !draft.banner.imageUrl.startsWith("data:")
          ? { imageUrl: draft.banner.imageUrl }
          : {}),
        ...(draft.bannerImageData ? { imageData: draft.bannerImageData } : {}),
      },
      salesLink: { activeTemplateId: draft.templateId, templates: [] },
    });
    const next = draftFrom({ ...customization, ...saved, salesLink: saved.salesLink });
    if (draft.bannerImageData && !next.banner.imageUrl) {
      next.banner = { enabled: draft.banner.enabled, imageUrl: draft.banner.imageUrl };
    }
    setDraft(next);
    setSavedId(next.templateId);
    setBaseline(JSON.stringify(previewDraft(next)));
  };

  if (!ready || !draft) {
    return <FullPageLoader text="Loading sales link" variant="contained" />;
  }

  return (
    <div className="space-y-5">
      <DashboardHead pageName="Sales link" />
      <PageHeader
        eyebrow="Sales link"
        title="Appearance"
        subtitle="Pick a template, then adjust colors, the hero, and the banner. The preview is the real page."
        actions={
          <div className="flex flex-wrap gap-2">
            {publicLink && (
              <>
                <Button
                  variant="outline"
                  className="rounded-full"
                  onClick={() => {
                    navigator.clipboard.writeText(publicLink).then(
                      () => toast.success("Sales link copied"),
                      () => toast.error("Couldn't copy the link"),
                    );
                  }}
                >
                  <RiFileCopyLine className="mr-1.5 size-4" />
                  Copy link
                </Button>
                <Button variant="outline" className="rounded-full" asChild>
                  <a href={publicLink} target="_blank" rel="noreferrer">
                    <RiExternalLinkLine className="mr-1.5 size-4" />
                    Open
                  </a>
                </Button>
              </>
            )}
            <Button className="rounded-full" disabled={!dirty || isUpdating} onClick={save}>
              {isUpdating ? "Saving…" : dirty ? "Save" : "Saved"}
            </Button>
          </div>
        }
      />

      <div className="grid gap-3 md:grid-cols-3">
        {SALES_LINK_TEMPLATES.map((template) => {
          const selected = draft.templateId === template.id;
          const live = savedId === template.id;
          return (
            <button
              key={template.id}
              type="button"
              onClick={() => setDraft({ ...draft, templateId: template.id })}
              className={cn(
                "rounded-[1.25rem] border bg-white p-3 text-left transition-colors",
                selected ? "border-primary ring-2 ring-primary/20" : "border-[#EBEBEB] hover:border-black/15",
              )}
            >
              <TemplateSketch id={template.id} color={draft.colors.primary} />
              <div className="mt-3 flex items-center gap-2">
                <p className="font-heading text-sm font-bold">{template.name}</p>
                {live && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#EDE4FB] px-2 py-0.5 text-[11px] font-semibold text-primary">
                    <RiCheckLine className="size-3" />
                    Live
                  </span>
                )}
                {selected && !live && (
                  <span className="rounded-full bg-[#F5F5F3] px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                    Selected
                  </span>
                )}
              </div>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{template.detail}</p>
            </button>
          );
        })}
      </div>

      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_400px]">
        <div className="space-y-4">
          <SectionCard className="space-y-4">
            <p className="font-heading text-sm font-bold">Colors</p>
            <div className="grid gap-3 sm:grid-cols-3">
              <ColorField label="Primary" value={draft.colors.primary} onChange={(primary) => setDraft({ ...draft, colors: { ...draft.colors, primary } })} />
              <ColorField label="Secondary" value={draft.colors.secondary} onChange={(secondary) => setDraft({ ...draft, colors: { ...draft.colors, secondary } })} />
              <ColorField label="Accent" value={draft.colors.accent} onChange={(accent) => setDraft({ ...draft, colors: { ...draft.colors, accent } })} />
            </div>
          </SectionCard>

          <SectionCard className="space-y-4">
            <p className="font-heading text-sm font-bold">Hero</p>
            <Field label="Title">
              <Input
                value={draft.hero.title}
                placeholder="Uses your store name when this is empty"
                onChange={(event) => setDraft({ ...draft, hero: { ...draft.hero, title: event.target.value } })}
              />
            </Field>
            <Field label="Subtitle">
              <Textarea
                value={draft.hero.subtitle}
                placeholder="Uses your store description when this is empty"
                onChange={(event) => setDraft({ ...draft, hero: { ...draft.hero, subtitle: event.target.value } })}
              />
            </Field>
            <div className="grid gap-2 sm:grid-cols-2">
              <Toggle label="Show hero" checked={draft.layoutOptions.showHero} onChange={(showHero) => setDraft({ ...draft, layoutOptions: { ...draft.layoutOptions, showHero } })} />
              <Toggle label="Category filter" checked={draft.layoutOptions.showCategories} onChange={(showCategories) => setDraft({ ...draft, layoutOptions: { ...draft.layoutOptions, showCategories } })} />
              <Toggle label="Search" checked={draft.layoutOptions.showSearch} onChange={(showSearch) => setDraft({ ...draft, layoutOptions: { ...draft.layoutOptions, showSearch } })} />
              <Toggle label="Social links" checked={draft.layoutOptions.showSocialLinks} onChange={(showSocialLinks) => setDraft({ ...draft, layoutOptions: { ...draft.layoutOptions, showSocialLinks } })} />
            </div>
          </SectionCard>

          <SectionCard className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-heading text-sm font-bold">Banner</p>
                <p className="text-xs text-muted-foreground">Shown in the hero when a photo is set.</p>
              </div>
              <Switch
                checked={draft.banner.enabled}
                onCheckedChange={(enabled) => setDraft({ ...draft, banner: { ...draft.banner, enabled } })}
              />
            </div>
            {draft.banner.enabled && (
              <BannerUpload
                value={null}
                onChange={() => {}}
                initialPreview={draft.bannerImageData || draft.banner.imageUrl}
                onProcessed={(imageData) => {
                  if (!imageData) return;
                  setDraft({
                    ...draft,
                    bannerImageData: imageData,
                    banner: { enabled: true, imageUrl: draft.banner.imageUrl },
                  });
                }}
              />
            )}
          </SectionCard>

          <SectionCard className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-heading text-sm font-bold">Announcement</p>
                <p className="text-xs text-muted-foreground">A bar across the top of the page.</p>
              </div>
              <Switch
                checked={draft.announcements.enabled}
                onCheckedChange={(enabled) => setDraft({ ...draft, announcements: { ...draft.announcements, enabled } })}
              />
            </div>
            {draft.announcements.enabled && (
              <>
                <Field label="Message">
                  <Input
                    value={draft.announcements.text}
                    onChange={(event) => setDraft({ ...draft, announcements: { ...draft.announcements, text: event.target.value } })}
                  />
                </Field>
                <div className="grid gap-3 sm:grid-cols-2">
                  <ColorField label="Background" value={draft.announcements.backgroundColor} onChange={(backgroundColor) => setDraft({ ...draft, announcements: { ...draft.announcements, backgroundColor } })} />
                  <ColorField label="Text" value={draft.announcements.textColor} onChange={(textColor) => setDraft({ ...draft, announcements: { ...draft.announcements, textColor } })} />
                </div>
              </>
            )}
          </SectionCard>
        </div>

        <div className="xl:sticky xl:top-4">
          <div className="overflow-hidden rounded-[1.75rem] border border-[#EBEBEB] bg-[#F5F5F3]">
            <div className="flex items-center justify-between px-4 py-2.5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Preview</p>
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground"
                onClick={() => {
                  setFrameReady(false);
                  setFrameKey((value) => value + 1);
                }}
              >
                <RiRefreshLine className="size-3.5" />
                Reload
              </button>
            </div>
            {currentStore?.url ? (
              <iframe
                key={`${currentStore.url}-${frameKey}`}
                ref={frameRef}
                title="Sales link preview"
                src={`/${currentStore.url}?preview=1`}
                className="h-[740px] w-full border-0 bg-white"
              />
            ) : (
              <div className="flex h-80 items-center justify-center px-6 text-center text-sm text-muted-foreground">
                Add a store link before the preview can load.
              </div>
            )}
          </div>
          <p className="mt-2 text-center text-xs text-muted-foreground">
            {dirty ? "Unsaved changes are visible here. Save to publish them." : "This is the page customers see."}
          </p>
        </div>
      </div>
    </div>
  );
}

function TemplateSketch({ id, color }: { id: SalesLinkTemplateId; color: string }) {
  if (id === "editorial") {
    return (
      <div className="h-28 rounded-xl bg-[#faf9f6] p-3">
        <div className="mx-auto h-2 w-16 rounded-full bg-black/15" />
        <div className="mx-auto mt-2 h-3 w-24 rounded-full" style={{ backgroundColor: color }} />
        <div className="mx-auto mt-3 h-12 w-full rounded-lg bg-black/10" />
      </div>
    );
  }
  if (id === "showcase") {
    return (
      <div className="grid h-28 grid-cols-5 gap-2 rounded-xl bg-[#F5F5F3] p-3">
        <div className="col-span-3 flex flex-col justify-center gap-2">
          <div className="h-2 w-10 rounded-full bg-black/15" />
          <div className="h-3 w-20 rounded-full" style={{ backgroundColor: color }} />
          <div className="h-2 w-16 rounded-full bg-black/10" />
        </div>
        <div className="col-span-2 rounded-lg" style={{ backgroundColor: color }} />
      </div>
    );
  }
  return (
    <div className="relative h-28 rounded-xl bg-[#F5F5F3] p-3">
      <div className="h-10 rounded-lg" style={{ backgroundColor: `${color}33` }} />
      <div className="absolute left-1/2 top-9 flex size-8 -translate-x-1/2 items-center justify-center rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: color }}>
        S
      </div>
      <div className="mx-auto mt-6 h-2 w-16 rounded-full bg-black/15" />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  return (
    <Field label={label}>
      <div className="flex gap-2">
        <input
          type="color"
          value={toPickerHex(value)}
          onChange={(event) => onChange(event.target.value)}
          className="h-10 w-12 cursor-pointer rounded-full border border-[#EBEBEB] bg-white p-1"
          aria-label={label}
        />
        <Input
          value={text}
          spellCheck={false}
          onChange={(event) => {
            const next = event.target.value;
            setText(next);
            if (isHexColor(next)) onChange(next);
          }}
          onBlur={() => {
            if (isHexColor(text)) onChange(text);
            else setText(value);
          }}
        />
      </div>
    </Field>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value: boolean) => void }) {
  return (
    <div className="flex items-center justify-between rounded-2xl bg-[#F5F5F3] px-3 py-2.5">
      <span className="text-sm font-medium">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function isHexColor(value: string) {
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value);
}

function toPickerHex(value: string) {
  if (/^#[0-9a-fA-F]{6}$/.test(value)) return value;
  if (/^#[0-9a-fA-F]{3}$/.test(value)) {
    return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`;
  }
  return "#6D28D9";
}
